<template>
  <div>
    <transition name="fade" mode="out-in">
      <div v-if="!loaded" key="loading">
        Loading...
      </div>

      <div class="subscription-info" v-else key="subscription-info">
        <Notification
          v-if="notification.active"
          :content="notification.content"
          :type="notification.type"
        />

        <div v-if="simpleMode">
          <div
            class="subscription-status"
            :class="statusClass"
          ></div>

          <h3 class="plan-price" v-if="!subscriptionCancelled">
            {{ planPrice }}<span>/{{ planInterval }}</span>
          </h3>

          <p v-if="subscriptionCancelled">
            Subscription Cancelled
          </p>

          <span
            class="plan-status-message"
            :class="statusClass"
          >
            {{status}}

            <span
              class="subscription-cancel-note"
              v-if="subscriptionWillCancel"
            >
              Cancelling soon
            </span>
          </span>

          <div class="subscription-cancel-timer" v-if="subscriptionWillCancel">
            <Sprite name="caution" />
          </div>
        </div>

        <div v-else>
          <div>
            <h2 class="overlay-heading" v-if="site">
              {{ site.name }} Website
              <span v-if="!subscriptionCancelled">{{ planPrice }}/{{ planInterval }}</span>
            </h2>
            <p v-if="discount">
              <strong>Discount applied:</strong> {{ discount }}
            </p>

            <div v-if="!hasDefaultPaymentMethod">
              <transition name="fade" mode="out-in">
                <div key="add-payment" v-if="addDefaultPaymentMethod">
                  <AddPaymentMethodForm
                    @createPaymentMethod="createPaymentMethod"
                    :paymentBtnLoading="paymentBtnLoading"
                    :showDefaultOption="false"
                    :stripe="stripe"
                    :siteId="siteId"
                    :companyId="companyId"
                    :customer="customer"
                  />
                </div>

                <div key="payment-notice" class="no-payment-method" v-else>
                  <p>You cannot upgrade as you don't have a default payment method yet.</p>
                  <Btn secondary @click="togglePaymentMethodForm">Add one</Btn>
                </div>
              </transition>
            </div>
          </div>

          <div v-if="subscriptionCancelled || subscriptionWillCancel">
            <p v-if="subscriptionWillCancel">
              This subscription will be cancelled at the end of the billing
              period, on <strong>{{ cancellationDate }}</strong>
            </p>
            <p v-else>
              This subscription has now been cancelled. You can re-activate it
              by choosing a plan.
            </p>
          </div>

          <div class="trial-progress" v-if="isTrialling">
            <div class="trial-progress-wrapper">
              <div class="trial-progress-bar" :style="barWidth"></div>
            </div>

            <span
              v-if="trialFinished && !subscriptionActive"
              class="plan-status-message"
            >
              <strong>Your trial has now expired!</strong>
              <span>Please add a payment method to keep online.</span>
            </span>

            <span
              v-if="trialFinished && !subscriptionActive"
              class="plan-status-message"
            >
              <strong>Your trial has now expired!</strong>
              <span>Please add a payment method to keep online.</span>
            </span>

            <span class="trial-status-message">
              You have
              <strong>{{ daysRemaining }} {{ daysRemainingUnit }}</strong> left
              of your trial.
            </span>

            <div v-if="hasDefaultPaymentMethod && !subscriptionWillCancel">
              <Btn @click="endTrial" class="btn-end-trial" primary :isLoading="btnEndTrialLoading">
                End Trial Now
              </Btn>
            </div>
          </div>

          <div v-if="hasDefaultPaymentMethod">
            <router-link
              class="btn is-tertiary"
              v-if="!subscriptionCancelled"
              :to="{
                name: 'invoice',
                params: {companyId: companyId, invoiceId: latestInvoice}
              }"
            >
              Invoices
            </router-link>

            <span v-if="subscriptionCancelled || subscriptionWillCancel">
              <Btn
                primary
                class="btn-pulse"
                :isLoading="btnDontCancelLoading"
                @click="reactivateSubscription"
                v-if="subscriptionWillCancel"
              >
                Don't Cancel
              </Btn>

              <router-link
                v-if="subscriptionCancelled"
                class="btn is-primary"
                :to="{name: 'edit-plan'}"
              >
                Choose Plan
              </router-link>
            </span>

            <span v-else>
              <router-link class="btn is-secondary" :to="{name: 'edit-plan'}">
                Change Plan
              </router-link>
            </span>

            <span>
              <router-link class="btn is-primary" :to="{name: 'site', params: {siteId: siteId}}">
                View Site
              </router-link>
            </span>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import moment from 'moment'
import {loadStripe} from '@stripe/stripe-js'

import Sprite from '@/components/Sprite'
import Btn from '@/components/Btn'
import AddPaymentMethodForm from '@/components/forms/AddPaymentMethodForm'

import notification from '@/mixins/notification'

import {
  attachCustomerPaymentMethodData,
  setDefaultCustomerPaymentMethod
} from '@/lib/payments/payment_methods'

import {
  readSiteSubscriptionData,
  endSiteSubscriptionTrial,
  reactivateSiteSubscriptionData
} from '@/lib/payments/subscriptions'
import {readStripeCustomerData} from '@/lib/payments/customers'

export default {
  name: 'SubscriptionInfo',

  mixins: [notification],

  data() {
    return {
      stripe: {},
      addDefaultPaymentMethod: false,
      btnDontCancelLoading: false,
      paymentBtnLoading: false,
      btnEndTrialLoading: false,
      stripeLoaded: false,
      publishableKey: process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY,
      customer: {},
      subscription: false,
      subscriptionCancelled: false
    }
  },

  props: {
    companyId: String,
    siteId: String,
    simpleMode: Boolean,
    site: Object
  },

  components: {
    Sprite,
    Btn,
    AddPaymentMethodForm
  },

  methods: {
    getData() {
      readSiteSubscriptionData(this.companyId, this.siteId)
        .then(({data}) => {
          this.subscription = data
        })
        .catch(({response}) => {
          this.subscriptionCancelled = true
        })

      readStripeCustomerData(this.companyId).then(({data}) => {
        this.customer = data
      })
    },

    createPaymentMethod(card, name) {
      this.paymentBtnLoading = true

      this.stripe
        .createPaymentMethod({
          type: 'card',
          card: card,
          billing_details: {
            name: name
          }
        })
        .then((result) => {
          if (result.error) {
            let message = result.error.message.replace('.', '')
            this.triggerNotification('error', 'card', message)
            this.paymentBtnLoading = false
          } else {
            let methodId = {
              payment_method_id: result.paymentMethod.id
            }

            // Attach payment method to customer
            attachCustomerPaymentMethodData(this.companyId, methodId).then(
              () => {
                // Set as default
                setDefaultCustomerPaymentMethod(this.companyId, methodId).then(
                  () => {
                    this.triggerNotification('success', 'default card', 'added')
                  }
                )

                window.setTimeout(() => {
                  this.addDefaultPaymentMethod = false
                  this.paymentBtnLoading = false

                  this.getData()
                }, this.closeDelay)
              }
            )
          }
        })
    },

    endTrial() {
      this.btnEndTrialLoading = true

      endSiteSubscriptionTrial(this.siteId)
        .then(({data}) => {
          this.triggerNotification('success', 'trial', 'ended', true)

          window.setTimeout(() => {
            this.btnEndTrialLoading = false

            this.$router.push({
              name: 'site',
              params: {siteId: this.siteId}
            })
          }, this.closeDelay)
        })
        .catch(({response}) => {
        })
    },

    reactivateSubscription() {
      this.btnDontCancelLoading = true

      reactivateSiteSubscriptionData(this.siteId).then(() => {
        this.triggerNotification('success', 'subscription', 're-activated', true)

        window.setTimeout(() => {
          this.getData()
          this.btnDontCancelLoading = false
        }, this.closeDelay)
      })
    },

    togglePaymentMethodForm() {
      this.addDefaultPaymentMethod = !this.addDefaultPaymentMethod
    }
  },

  computed: {
    customerLoaded() {
      return !!this.customer.id
    },

    status() {
      return this.subscriptionCancelled ? 'cancelled' : this.subscription.status
    },

    statusClass() {
      return this.subscriptionCancelled ? 'is-canceled' : `is-${this.subscription.status}`
    },

    loaded() {
      return this.customerLoaded && this.stripeLoaded
    },

    trialStartDate() {
      return moment.unix(this.subscription.trial_start).format('YYYY-MM-DD')
    },

    trialEndDate() {
      return moment.unix(this.subscription.trial_end).format('YYYY-MM-DD')
    },

    subscriptionWillCancel() {
      return this.subscription.cancel_at_period_end
    },

    cancellationDate() {
      return moment
        .unix(this.subscription.cancel_at)
        .format('dddd DD MMMM YYYY')
    },

    latestInvoice() {
      return this.subscription.plan ? this.subscription.latest_invoice : ''
    },

    planPrice() {
      return this.subscription.plan
        ? `£${this.subscription.plan.amount / 100}`
        : ''
    },

    planInterval() {
      return this.subscription.plan ? this.subscription.plan.interval : ''
    },

    discount() {
      return this.subscription.discount
        ? this.subscription.discount.coupon.name
        : false
    },

    isTrialling() {
      return this.subscription ? this.subscription.status === 'trialing' : false
    },

    subscriptionActive() {
      return this.subscription ? this.subscription.status === 'active' : false
    },

    hasDefaultPaymentMethod() {
      return !!this.customer.invoice_settings.default_payment_method
    },

    daysSpent() {
      if (this.isTrialling) {
        let start = this.subscription.trial_start
        let startDate = moment.unix(start).format('YYYY-MM-DD')
        let now = moment().format('YYYY-MM-DD')

        return moment(now).diff(startDate, 'days')
      }

      return 0
    },

    daysRemaining() {
      return this.trialPeriod - this.daysSpent
    },

    trialPeriod() {
      if (this.isTrialling) {
        let start = this.subscription.trial_start
        let end = this.subscription.trial_end
        let startDate = moment.unix(start).format('YYYY-MM-DD')
        let endDate = moment.unix(end).format('YYYY-MM-DD')

        return moment(endDate).diff(startDate, 'days')
      }

      return 0
    },

    daysRemainingUnit() {
      return this.daysRemaining === 1 ? 'day' : 'days'
    },

    trialFinished() {
      return this.daysRemaining <= 0
    },

    barWidth() {
      let width = (100 / this.trialPeriod) * this.daysSpent
      return `width: ${width}%`
    }
  },

  mounted() {
    loadStripe(this.publishableKey).then((result) => {
      this.stripe = result
      this.stripeLoaded = true
    })

    this.getData()
  }
}
</script>

<style lang="sass" scoped>
@import '../stylesheets/base/variables'
@import '../stylesheets/helpers/mixins'
@import '../stylesheets/helpers/extends'
@import '../stylesheets/base/theme'

$progress-bar-height: 15px

.no-payment-method
  background: rgba(0, 0, 0, 0.05)
  margin-top: $gap-small
  padding: $gap-small
  border-radius: 3px

.subscription-info
  text-align: center
  width: 100%

  .btn
    margin: $gap-x-small

.overlay-heading
  span
    opacity: 0.5
    margin-left: $gap-x-small

.btn-end-trial
  margin: $gap-small auto $gap-medium
  animation: pulse 1s infinite ease-in-out

.subscription-cancel-timer
  position: absolute
  bottom: $gap-small
  left: $gap-small

  .sprite
    fill: $color-trialing
    position: relative
    top: 5px
    width: 16px
    height: 16px

.subscription-cancel-note
  opacity: 0
  background: #fff
  color: $color-trialing
  position: absolute
  width: 100%
  right: 0
  width: 110px
  transition: all .2s ease-in-out

.subscription-status
  @extend %active-state

.plan-price
  font-size: 24px
  margin: 0 0 $gap-medium
  opacity: 0.75
  transition: opacity .2s ease-in-out

  span
    font-size: 14px
    position: relative
    top: -3px
    left: 2px

.plan-status-message, .trial-status-message
  display: block
  font-size: 12px

.trial-status-message
  margin: $gap-small 0

.trial-progress-wrapper
  border: 1px solid $color-border
  height: $progress-bar-height
  padding: 1px
  border-radius: 8px
  margin-top: $gap-small

.trial-progress-bar
  border-radius: 5px
  height: 100%

  @include themify($themes)
    background: themed('color-primary')
</style>
